import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, firstValueFrom, map, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private userProfile$: Promise<any> = this.setUser();
  // Static list of skill references (sorted in order to display)
  // Could be call to database, but static for now
  skillsList: any;
  skillNames: { [id: string]: any } = {}

  skillIdMap = new Map<number, string>();

  baerRoles: any;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly authService: AuthService,
  ) {
    this.httpClient.get(`${environment.apiUrl}/metadata/skills`).subscribe((skills: any) => {
      this.skillsList = skills;
      this.skillsList.forEach((item: any) => {
        this.skillNames[item.text] = item.value
      })
      for (let skill in this.skillNames) {
        this.skillIdMap.set(this.skillNames[skill], skill)
      }
    });
    let params = new HttpParams();
    this.httpClient.get(
      `${environment.apiUrl}/metadata/baer-roles`, { params: params }
    ).subscribe((roles: any) => { this.baerRoles = roles });
  }

  getUserProfile(): Promise<any> {
    return this.userProfile$;
  }

  refreshUserProfile(): Promise<any> {
    this.userProfile$ = this.setUser();
    return this.userProfile$;
  }

  private async setUser() {
    try {
      return await firstValueFrom(this.getLoginUser().pipe(map(user => {

        user.baer_roles.forEach((position: any) => {
          if (position.location_id == null) {
            position.location_id = user.location_id;
            position.region = user.region;
            position.forest_name = user.forest_name;
          }
        })
        // 0-Reporting Tool Administrator
        // 3-Washington Office Director
        // 12-WO Coordination
        // 13-WO Logistics
        user.isWO = user.baer_roles.find((obj: any) => [0, 3, 12, 13].includes(obj.baer_role.value));
        // 0-Reporting Tool Administrator
        // 3-Washington Office Director
        // 4- Forest BAER Coordinator
        // 8- RO BAER Coordinator
        // 9 - RO BAR Coordinator
        // 11 - RO Disaster Coordinator
        // 12-WO Coordination
        // 13-WO Logistics
        user.isCO = user.baer_roles.find((obj: any) => [0, 3, 4, 8, 12, 13].includes(obj.baer_role.value))
        // 14-Forest Unit Line Officer
        // 15-Acting Forest Unit Line Officer
        // 16-Regional Line Officer
        // 17-Acting Regional Line Officer
        // 18-wo Line Officer
        // 19-Acting wo Line Officer
        user.isLO = user.baer_roles.find((obj: any) => [14, 5, 16, 17, 18, 19].includes(obj.baer_role.value))
        // 0-Reporting Tool Administrator
        // 3-Washington Office Director
        // 12-WO Coordination
        user.isWOCO = user.baer_roles.filter((obj: any) => [0, 3, 12].includes(obj.baer_role.value))
        // 8- RO BAER Coordinator
        // 9 - RO BAR Coordinator
        // 11 - RO Disaster Coordinator
        user.isROCO = user.baer_roles.filter((obj: any) => [8, 9, 11].includes(obj.baer_role.value))?.map((x: any) => x.region ?? user.region) ?? [];
        // 4- Forest BAER Coordinator
        // 5-Forest BAR Coordinator
        user.isFOCO = user.baer_roles.filter((obj: any) => [4, 5].includes(obj.baer_role.value))?.map((x: any) => x.forest_name ?? user.forest_name) ?? [];
        // 1-BAER Team Leader
        user.teamLeadRoles = user.baer_roles.find((obj: any) => [1].includes(obj.baer_role.value));
        // 0-BAER Reporting Tool Administrator
        // 3-Washington Office Director
        // 12-WO Coordination
        // 13-WO Logistics
        // 18-Washington Office Line Officer
        // 19-Acting Washington Office Line Officer
        user.woOffice = user.baer_roles.find((obj: any) => [0, 3, 12, 13, 18, 19].includes(obj.baer_role.value))
        // 8-RO BAER Coordinator
        // 9-RO BAR Coordinator
        // 10-RO READ Coordinator
        // 11-RO Disaster Coordinator
        // 16-Regional Line Officer
        // 17-Acting Regional Line Officer
        user.regionOffice = user.baer_roles.find((obj: any) => [8, 9, 10, 11, 16, 17].includes(obj.baer_role.value))
        // 4- Forest BAER Coordinator
        // 5-Forest BAR Coordinator
        // 14-Forest Unit Line Officer
        // 15-Acting Forest Unit Line Officer
        user.forestOffice = user.baer_roles.find((obj: any) => [4, 5, 14, 15].includes(obj.baer_role.value))
        // 14-Forest Unit Line Officer
        // 15-Acting Forest Unit Line Officer
        user.isFOLO = user.baer_roles.find((obj: any) => [14, 15].includes(obj.baer_role.value))
        // 16-Regional Line Officer
        // 17-Acting Regional Line Officer
        user.isROLO = user.baer_roles.find((obj: any) => [16, 17].includes(obj.baer_role.value))
        // 18-wo Line Officer
        // 19-Acting wo Line Officer
        user.isWOLO = user.baer_roles.find((obj: any) => [18, 19].includes(obj.baer_role.value))



        user.showRoles = [];
        user.baer_roles?.forEach((role: any) => {
          if ([4, 5].includes(role.value))
            user.showRoles.push(role.baer_role.text);
        })
        //setup editability
        // 0-BAER Reporting Tool Administrator
        // 3-Washington Office Director
        // 4-Forest BAER Coordinator
        // 5-Forest BAR Coordinator
        // 6-Forest READ Coordinator
        // 7-Forest Disaster Coordinator
        // 8-RO BAER Coordinator
        // 9-RO BAR Coordinator
        // 10-RO READ Coordinator
        // 11-RO Disaster Coordinator
        // 12-WO Coordination
        // 13-WO Logistics
        user.canEditRegion = user.baer_roles?.filter((obj: any) => [0, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].includes(obj.baer_role.value))?.map((x: any) => x.region ?? user.region) ?? []
        user.canEditForest = user.baer_roles?.filter((obj: any) => [0, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].includes(obj.baer_role.value))?.map((x: any) => x.forest_name ?? user.forest_name) ?? []
        user.canDelete = user.baer_roles?.find((obj: any) => [0, 3, 8, 9, 10, 11, 12, 13].includes(obj.baer_role.value))
        return user;
      })));
    }
    catch {
      console.log('no user returned. signing out');
      this.authService.logout();
    }
  }
  /**
   * GET request for user data
   *
   * @returns Promise of list of users
   */
  getUserList(type?: string, relations?: string[], page?: number, pageSize?: number, filters?: any, sort?: string, sortDirection?: string): Observable<any> {
    let params = new HttpParams()
    let queryParams = ""
    if (page) {
      queryParams += "page=" + page
    }
    if (pageSize) {
      queryParams = this.checkForBlank(queryParams);
      queryParams += "pageSize=" + pageSize
    }
    if (filters) {
      for (let filter in filters) {
        if (filters[filter] !== null) {
          queryParams = this.checkForBlank(queryParams);
          queryParams += `${filter}=${filters[filter]}`
        }
      }
    }
    if (sort) {
      queryParams = this.checkForBlank(queryParams);
      queryParams += "sort=" + sort
    }
    if (sortDirection) {
      queryParams = this.checkForBlank(queryParams);
      queryParams += "sortDirection=" + sortDirection
    }
    if (type) {
      queryParams = this.checkForBlank(queryParams);
      queryParams += "roster_type=" + type;
    }
    if (relations) {
      queryParams = this.checkForBlank(queryParams);
      queryParams += "relations=" + relations.join(",");
    }
    if (queryParams != "") {
      queryParams = "?" + queryParams
    }
    return this.httpClient.get(
      `${environment.apiUrl}/user${queryParams}`, { params: params }
    );
  }
  /**
   * GET request for user data. Scans all pages of lead users.
   *
   * @returns Promise of list of all active Leads
   */
  getPossibleLeadList(relations?: string[]): Observable<any> {
    let params = new HttpParams()
    let queryParams = "?isTeamLeader=true"
    if (relations) {
      queryParams = this.checkForBlank(queryParams);
      queryParams += "relations=" + relations.join(",")
    }
    return this.httpClient.get(
      `${environment.apiUrl}/user${queryParams}`, { params: params }
    );
  }

  checkForBlank(queryParams: string) {
    if (queryParams != "") queryParams += "&"
    return queryParams
  }

  getUsersByEmail(email: string): Observable<any> {
    let params = new HttpParams()
    let queryParams = "?email=" + email
    queryParams = queryParams.replaceAll("@", "%40")
    queryParams = this.checkForBlank(queryParams);
    queryParams += "relations=skills"
    return this.httpClient.get(
      `${environment.apiUrl}/user${queryParams}`, { params: params }
    );
  }

  getUser(user_id: number, relations?: string[]): Observable<any> {
    let params = new HttpParams()
    let queryParams = "?user_id=" + user_id
    queryParams = queryParams.replaceAll("@", "%40")
    if (relations) {
      queryParams = this.checkForBlank(queryParams);
      queryParams += "relations=" + relations.join(",")
    }
    return this.httpClient.get(
      `${environment.apiUrl}/user${queryParams}`, { params: params }
    );
  }

  getLoginUser(): Observable<any> {
    let params = new HttpParams()
    return this.httpClient.get(
      `${environment.apiUrl}/auth/user`, { params: params }
    );
  }

  /**
   * Sends POST request to create new user
   *
   * @param data body of request
   * @returns Promise indicating success/failure
   */
  createNewUser(data: any): Observable<any> {
    let params = new HttpParams()
    return this.httpClient.post(
      `${environment.apiUrl}/user`, data, { params: params }
    );
  }

  /**
   * Sends DELETE request to edit user specified by user_id
   *
   * @param data body of request
   * @returns Promise indicating success/failure
   */
  deleteUser(data: {
    user_id_list: number[]
  }): Observable<any> {
    return this.httpClient.delete(
      `${environment.apiUrl}/user`, {
      body: data
    }
    );
  }

  /**
   * Sends PUT request to edit user specified by user_id
   *
   * @param data body of request
   * @returns Promise indicating success/failure
   */
  editUserDetails(data: any): Observable<any> {
    let params = new HttpParams()
    return this.httpClient.put(
      `${environment.apiUrl}/user`, data, { params: params }
    );
  }

  getAgencyList(): Array<object> {
    return [
      { text: "USFS", value: 0 },
      { text: "BLM", value: 1 },
      { text: "FWS", value: 2 },
      { text: "USGS", value: 3 },
      { text: "NWS", value: 4 },
      { text: "NPS", value: 5 },
      { text: "NRCS", value: 6 },
      { text: "Not Applicable (AD)", value: 7 }
    ];
  }

  getExpList(): Array<object> {
    return [
      { text: "Trainee", value: 0 },
      { text: "1 to 5 assignments", value: 1 },
      { text: "6 to 10 assignments", value: 2 },
      { text: "10+ assignments", value: 3 },
    ];
  }

  getSkillList(): Observable<Array<object>> {
    return of(this.skillsList);
  }

  /**
   * Returns Map of skill_id to skill text
   *
   * @returns number to string map
   */
  getSkillIdMap(): Map<number, string> {
    return this.skillIdMap
  }

  /**
   * Returns dictionary of skills, indexed by name
   *
   * @returns dictionary of skills with string indexes
   */
  getSkillNames(): { [id: string]: any } {
    return this.skillNames
  }

  getRoleList(): any {
    return this.baerRoles;
    // {value: 0, text: 'BAER Reporting Tool Administrator', type: 'BAER'}
    // {value: 1, text: 'BAER Team Leader', type: 'BAER'}
    // {value: 2, text: 'BAER Team Members', type: 'BAER'}
    // {value: 3, text: 'Washington Office Director', type: 'WOCO'}
    // {value: 4, text: 'Forest BAER Coordinator', type: 'FOCO'}
    // {value: 5, text: 'Forest BAR Coordinator', type: 'FOCO'}
    // {value: 6, text: 'Forest READ Coordinator', type: 'FOCO'}
    // {value: 7, text: 'Forest Disaster Coordinator', type: 'FOCO'}
    // {value: 8, text: 'RO BAER Coordinator', type: 'ROCO'}
    // {value: 9, text: 'RO BAR Coordinator', type: 'ROCO'}
    // {value: 10, text: 'RO READ Coordinator', type: 'ROCO'}
    // {value: 11, text: 'RO Disaster Coordinator', type: 'ROCO'}
    // {value: 12, text: 'WO Coordination', type: 'WOCO'}
    // {value: 13, text: 'WO Logistics', type: 'WOCO'}
    // {value: 14, text: 'Forest Unit Line Officer', type: 'FOLO'}
    // {value: 15, text: 'Acting Forest Unit Line Officer', type: 'FOLO'}
    // {value: 16, text: 'Regional Line Officer', type: 'ROLO'}
    // {value: 17, text: 'Acting Regional Line Officer', type: 'ROLO'}
    // {value: 18, text: 'Washington Office Line Officer', type: 'WOLO'}
    // {value: 19, text: 'Acting Washington Office Line Officer', type: 'WOLO'}
  }

  checkAgencyRegionForest(agency: any, location_id: any, region: any, forest: any): boolean {
    if (agency == 0) { //user is USFS
      if (region == 'WO') return true; //WO no forest
      if (region != '') { //region selected
        if (location_id == 0 && forest != '') return true; //forest selected
        if (location_id == 1) return true; //region selected
      }
    }
    else if (agency != '') { //agency is selected - not USFS
      return true;
    }
    return false; //did not hit valid value combination
  }

  getGACCList(): Observable<any> {
    let params = new HttpParams()
    return this.httpClient.get(
      `${environment.apiUrl}/metadata/GACC-dispatch-centers`, { params: params }
    );
  }

  deleteBAERRole(user_id: number, baer_role_id: number): Observable<any> {
    let data = {
      user_baer_role_id_list: [baer_role_id]
    }
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: data // The body is passed here
    };
    return this.httpClient.delete(
      `${environment.apiUrl}/user/${user_id}/role`, options
    );
  }
}
